import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { ArrowLeft } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_marketing-economy/artifacts/q6npai4a_907F1F75-F4C3-4C66-925B-49CBEC490807.PNG";

const PrivacyPolicy = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      title: "Politique de confidentialité",
      lastUpdated: "Dernière mise à jour : Décembre 2025",
      backToHome: "Retour à l'accueil",
      sections: [
        {
          title: "1. Introduction",
          content: `AQERA Inc. (« AQERA », « nous », « notre » ou « nos ») s'engage à protéger la vie privée des utilisateurs de sa plateforme. Cette politique de confidentialité explique comment nous recueillons, utilisons, divulguons et protégeons vos renseignements personnels conformément à la Loi sur la protection des renseignements personnels dans le secteur privé du Québec (Loi 25), à la Loi sur la protection des renseignements personnels et les documents électroniques (LPRPDE) et aux autres lois applicables.`
        },
        {
          title: "2. Renseignements que nous recueillons",
          content: `Nous recueillons les types de renseignements suivants :

**Renseignements fournis directement :**
• Nom complet et coordonnées (adresse courriel, numéro de téléphone)
• Informations de compte (identifiants, mot de passe)
• Informations de paiement et de facturation
• Informations sur votre entreprise (pour les partenaires commerciaux)
• Communications avec notre service à la clientèle

**Renseignements recueillis automatiquement :**
• Adresse IP et données de localisation approximative
• Type d'appareil, système d'exploitation et navigateur
• Données d'utilisation de la plateforme
• Cookies et technologies similaires`
        },
        {
          title: "3. Utilisation des renseignements",
          content: `Nous utilisons vos renseignements personnels aux fins suivantes :

• Fournir, maintenir et améliorer nos services
• Traiter les transactions et gérer votre compte
• Créer et gérer les missions marketing
• Calculer et distribuer les récompenses
• Communiquer avec vous concernant votre compte et nos services
• Assurer la sécurité et prévenir la fraude
• Respecter nos obligations légales et réglementaires
• Analyser l'utilisation de notre plateforme pour l'améliorer`
        },
        {
          title: "4. Base juridique du traitement",
          content: `Conformément à la Loi 25 du Québec, nous traitons vos renseignements personnels sur les bases suivantes :

• **Consentement** : Pour certaines activités de marketing et communications promotionnelles
• **Exécution d'un contrat** : Pour fournir nos services et traiter les transactions
• **Intérêts légitimes** : Pour améliorer nos services et assurer la sécurité
• **Obligations légales** : Pour respecter les lois fiscales et autres réglementations`
        },
        {
          title: "5. Partage des renseignements",
          content: `Nous pouvons partager vos renseignements avec :

• **Partenaires commerciaux** : Les commerces participants pour l'exécution des missions
• **Fournisseurs de services** : Hébergement, paiement, analyse, service client
• **Autorités légales** : Lorsque requis par la loi ou pour protéger nos droits

Nous ne vendons pas vos renseignements personnels à des tiers.`
        },
        {
          title: "6. Conservation des données",
          content: `Nous conservons vos renseignements personnels aussi longtemps que nécessaire pour :

• Fournir nos services et maintenir votre compte actif
• Respecter nos obligations légales (généralement 7 ans pour les données fiscales)
• Résoudre les litiges et faire respecter nos ententes

Après cette période, vos données sont supprimées ou anonymisées de manière sécurisée.`
        },
        {
          title: "7. Vos droits",
          content: `Conformément à la Loi 25 du Québec, vous disposez des droits suivants :

• **Droit d'accès** : Obtenir une copie de vos renseignements personnels
• **Droit de rectification** : Corriger des renseignements inexacts ou incomplets
• **Droit à l'effacement** : Demander la suppression de vos données dans certaines circonstances
• **Droit à la portabilité** : Recevoir vos données dans un format structuré et lisible
• **Droit de retirer votre consentement** : À tout moment, sans affecter la légalité du traitement antérieur
• **Droit de déposer une plainte** : Auprès de la Commission d'accès à l'information du Québec

Pour exercer ces droits, contactez-nous à : privacy@aqera.app`
        },
        {
          title: "8. Sécurité des données",
          content: `Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos renseignements, notamment :

• Chiffrement des données en transit et au repos
• Contrôles d'accès stricts
• Surveillance continue de nos systèmes
• Formation régulière de notre personnel
• Évaluations de sécurité périodiques`
        },
        {
          title: "9. Transferts internationaux",
          content: `Vos données peuvent être traitées par des fournisseurs situés à l'extérieur du Québec ou du Canada. Dans ce cas, nous nous assurons que des mesures de protection adéquates sont en place conformément aux exigences de la Loi 25.`
        },
        {
          title: "10. Cookies et technologies similaires",
          content: `Nous utilisons des cookies et technologies similaires pour :

• Assurer le bon fonctionnement de la plateforme
• Mémoriser vos préférences
• Analyser l'utilisation de nos services
• Améliorer votre expérience utilisateur

Vous pouvez gérer vos préférences de cookies via les paramètres de votre navigateur.`
        },
        {
          title: "11. Modifications de cette politique",
          content: `Nous pouvons modifier cette politique de confidentialité périodiquement. Toute modification importante vous sera notifiée par courriel ou via notre plateforme. La date de « dernière mise à jour » en haut de cette page indique quand cette politique a été révisée pour la dernière fois.`
        },
        {
          title: "12. Responsable de la protection des renseignements personnels",
          content: `Conformément à la Loi 25, nous avons désigné un responsable de la protection des renseignements personnels. Pour toute question concernant cette politique ou vos renseignements personnels :

**AQERA Inc.**
Responsable de la protection des renseignements personnels
Courriel : privacy@aqera.app
Montréal, Québec, Canada`
        }
      ]
    },
    en: {
      title: "Privacy Policy",
      lastUpdated: "Last updated: December 2025",
      backToHome: "Back to home",
      sections: [
        {
          title: "1. Introduction",
          content: `AQERA Inc. ("AQERA", "we", "our" or "us") is committed to protecting the privacy of users of its platform. This privacy policy explains how we collect, use, disclose and protect your personal information in accordance with Quebec's Act respecting the protection of personal information in the private sector (Law 25), the Personal Information Protection and Electronic Documents Act (PIPEDA) and other applicable laws.`
        },
        {
          title: "2. Information We Collect",
          content: `We collect the following types of information:

**Information provided directly:**
• Full name and contact information (email address, phone number)
• Account information (identifiers, password)
• Payment and billing information
• Business information (for business partners)
• Communications with our customer service

**Information collected automatically:**
• IP address and approximate location data
• Device type, operating system and browser
• Platform usage data
• Cookies and similar technologies`
        },
        {
          title: "3. Use of Information",
          content: `We use your personal information for the following purposes:

• Provide, maintain and improve our services
• Process transactions and manage your account
• Create and manage marketing missions
• Calculate and distribute rewards
• Communicate with you about your account and our services
• Ensure security and prevent fraud
• Comply with our legal and regulatory obligations
• Analyze platform usage to improve it`
        },
        {
          title: "4. Legal Basis for Processing",
          content: `In accordance with Quebec's Law 25, we process your personal information on the following bases:

• **Consent**: For certain marketing activities and promotional communications
• **Contract performance**: To provide our services and process transactions
• **Legitimate interests**: To improve our services and ensure security
• **Legal obligations**: To comply with tax laws and other regulations`
        },
        {
          title: "5. Information Sharing",
          content: `We may share your information with:

• **Business partners**: Participating businesses for mission execution
• **Service providers**: Hosting, payment, analytics, customer service
• **Legal authorities**: When required by law or to protect our rights

We do not sell your personal information to third parties.`
        },
        {
          title: "6. Data Retention",
          content: `We retain your personal information as long as necessary to:

• Provide our services and maintain your active account
• Comply with our legal obligations (generally 7 years for tax data)
• Resolve disputes and enforce our agreements

After this period, your data is securely deleted or anonymized.`
        },
        {
          title: "7. Your Rights",
          content: `In accordance with Quebec's Law 25, you have the following rights:

• **Right of access**: Obtain a copy of your personal information
• **Right of rectification**: Correct inaccurate or incomplete information
• **Right to erasure**: Request deletion of your data in certain circumstances
• **Right to portability**: Receive your data in a structured, readable format
• **Right to withdraw consent**: At any time, without affecting the legality of prior processing
• **Right to file a complaint**: With the Commission d'accès à l'information du Québec

To exercise these rights, contact us at: privacy@aqera.app`
        },
        {
          title: "8. Data Security",
          content: `We implement appropriate security measures to protect your information, including:

• Encryption of data in transit and at rest
• Strict access controls
• Continuous monitoring of our systems
• Regular staff training
• Periodic security assessments`
        },
        {
          title: "9. International Transfers",
          content: `Your data may be processed by providers located outside Quebec or Canada. In this case, we ensure that adequate protective measures are in place in accordance with Law 25 requirements.`
        },
        {
          title: "10. Cookies and Similar Technologies",
          content: `We use cookies and similar technologies to:

• Ensure proper platform operation
• Remember your preferences
• Analyze use of our services
• Improve your user experience

You can manage your cookie preferences through your browser settings.`
        },
        {
          title: "11. Changes to This Policy",
          content: `We may modify this privacy policy periodically. Any significant changes will be notified to you by email or through our platform. The "last updated" date at the top of this page indicates when this policy was last revised.`
        },
        {
          title: "12. Personal Information Protection Officer",
          content: `In accordance with Law 25, we have designated a personal information protection officer. For any questions about this policy or your personal information:

**AQERA Inc.**
Personal Information Protection Officer
Email: privacy@aqera.app
Montreal, Quebec, Canada`
        }
      ]
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-[#020408]">
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="section-container py-6">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="AQERA" className="h-10 w-auto" />
            </a>
            <a 
              href="/" 
              className="flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.backToHome}
            </a>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="section-container py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{t.title}</h1>
          <p className="text-[#64748B] mb-12">{t.lastUpdated}</p>
          
          <div className="space-y-10">
            {t.sections.map((section, index) => (
              <section key={index} className="prose prose-invert max-w-none">
                <h2 className="text-xl font-semibold text-white mb-4">{section.title}</h2>
                <div className="text-[#94A3B8] leading-relaxed whitespace-pre-line">
                  {section.content.split('**').map((part, i) => 
                    i % 2 === 1 ? <strong key={i} className="text-white">{part}</strong> : part
                  )}
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="section-container">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <img src={LOGO_URL} alt="AQERA" className="h-8 w-auto" />
            <p className="text-[#64748B] text-sm">© 2025 AQERA Inc. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
